/**
 * PNG tEXt/zTXt 청크 추출 — 결정 #6 카드 임포트의 컨테이너 파서.
 *
 * 캐릭터 카드 PNG는 메타데이터를 PNG `tEXt`(비압축) 또는 `zTXt`(압축) 청크에
 * `keyword\0(base64 payload)` 형태로 임베드한다(CCv3: `ccv3`, 구버전: `chara`).
 * 플랫폼 중립(결정 #10): DOM/Node 의존 없이 바이트만 다룬다. zlib(zTXt)은
 * 브라우저 표준이 없으므로 zTXt는 호출부에서 DecompressionStream으로 후처리할 수
 * 있도록 압축 바이트를 그대로 돌려준다(여기서는 tEXt 위주, 대부분 tEXt 사용).
 */

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

export interface PngTextChunk {
  keyword: string;
  /** tEXt면 평문 텍스트, zTXt면 압축 해제 전 raw 바이트(deflate). */
  text?: string;
  compressed?: Uint8Array;
}

/** 바이트가 PNG 시그니처로 시작하는지. */
export function isPng(bytes: Uint8Array): boolean {
  if (bytes.length < 8) return false;
  for (let i = 0; i < 8; i++) if (bytes[i] !== PNG_SIGNATURE[i]) return false;
  return true;
}

const decoderLatin1 = new TextDecoder("latin1");

/** PNG의 모든 tEXt/zTXt 청크를 추출한다. PNG가 아니면 빈 배열. */
export function extractPngTextChunks(bytes: Uint8Array): PngTextChunk[] {
  if (!isPng(bytes)) return [];
  const chunks: PngTextChunk[] = [];
  let offset = 8; // 시그니처 다음부터

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  while (offset + 8 <= bytes.length) {
    const length = view.getUint32(offset);
    const type = decoderLatin1.decode(bytes.subarray(offset + 4, offset + 8));
    const dataStart = offset + 8;
    const dataEnd = dataStart + length;
    if (dataEnd > bytes.length) break;

    if (type === "tEXt") {
      const data = bytes.subarray(dataStart, dataEnd);
      const nul = data.indexOf(0);
      if (nul !== -1) {
        const keyword = decoderLatin1.decode(data.subarray(0, nul));
        // payload는 latin1이 아니라 UTF-8일 수 있으므로 별도 디코드.
        const text = new TextDecoder("utf-8").decode(data.subarray(nul + 1));
        chunks.push({ keyword, text });
      }
    } else if (type === "zTXt") {
      const data = bytes.subarray(dataStart, dataEnd);
      const nul = data.indexOf(0);
      if (nul !== -1) {
        const keyword = decoderLatin1.decode(data.subarray(0, nul));
        // data[nul+1] = 압축 방식(0=deflate), 이후가 압축 바이트.
        const compressed = data.subarray(nul + 2);
        chunks.push({ keyword, compressed: compressed.slice() });
      }
    } else if (type === "IEND") {
      break;
    }

    offset = dataEnd + 4; // 데이터 + CRC(4바이트)
  }

  return chunks;
}

/** base64 문자열을 raw 바이트로 디코드(임베드 에셋 복원용). */
export function base64ToBytes(b64: string): Uint8Array {
  // data URI 접두(`data:image/png;base64,`)가 붙어 있으면 떼어낸다.
  let clean = b64.trim();
  const comma = clean.indexOf(",");
  if (clean.startsWith("data:") && comma !== -1) clean = clean.slice(comma + 1);
  // atob은 브라우저/RN(polyfill)/Node20 모두 사용 가능.
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** base64 문자열을 UTF-8 텍스트로 디코드(카드 JSON 복원용). */
export function base64ToUtf8(b64: string): string {
  return new TextDecoder("utf-8").decode(base64ToBytes(b64));
}

/** PNG 추가 에셋 청크의 keyword 접두(결정 #6). `chara-ext-asset_:{path}`. */
export const PNG_ASSET_KEYWORD_PREFIX = "chara-ext-asset_:";

/** PNG 임베드 에셋 1건: 청크 경로(에셋 URI가 가리키는 키) + 디코드된 바이트. */
export interface PngEmbeddedAsset {
  /** `chara-ext-asset_:` 접두를 뗀 경로(카드 JSON의 `embeded://{path}`와 매칭). */
  path: string;
  bytes: Uint8Array;
}

/**
 * PNG text 청크들에서 임베드 에셋(`chara-ext-asset_:{path}`)을 뽑아 base64 디코드한다
 * (결정 #6). zTXt(압축) 에셋 청크는 {@link extractPngTextChunksDecoded}로 미리 풀린
 * 경우에만 잡힌다(동기 경로에서는 건너뜀). 디코드 실패 청크는 조용히 무시한다.
 */
export function extractPngEmbeddedAssets(chunks: PngTextChunk[]): PngEmbeddedAsset[] {
  const out: PngEmbeddedAsset[] = [];
  for (const c of chunks) {
    if (!c.keyword.startsWith(PNG_ASSET_KEYWORD_PREFIX)) continue;
    if (typeof c.text !== "string") continue;
    const path = c.keyword.slice(PNG_ASSET_KEYWORD_PREFIX.length);
    try {
      out.push({ path, bytes: base64ToBytes(c.text) });
    } catch {
      // base64가 아니거나 깨진 청크는 스킵(임포트 전체를 죽이지 않음).
    }
  }
  return out;
}

/**
 * zlib(deflate) 바이트를 압축 해제한다(zTXt 청크용). 표준 `DecompressionStream`을
 * 쓰되, 없는 환경(예: 6s+ 기준 Safari 15)에서는 undefined를 반환해 호출부가
 * zTXt를 우아하게 건너뛰도록 한다. 대부분의 카드 PNG는 비압축 tEXt를 쓴다.
 */
export async function inflateZlib(data: Uint8Array): Promise<Uint8Array | undefined> {
  const DS = (globalThis as { DecompressionStream?: typeof DecompressionStream })
    .DecompressionStream;
  if (typeof DS !== "function") return undefined;
  try {
    // 새 ArrayBuffer로 복사(TS 5.7 typed-array 호환 + SharedArrayBuffer 회피).
    const buf = new Uint8Array(data.byteLength);
    buf.set(data);
    const stream = new Blob([buf]).stream().pipeThrough(new DS("deflate"));
    const out = new Uint8Array(await new Response(stream).arrayBuffer());
    return out;
  } catch {
    return undefined;
  }
}

/**
 * PNG의 모든 text 청크를 추출하되, zTXt(압축)는 압축 해제해 `text`로 채워 반환한다
 * (결정 #6). 압축 해제가 불가능한 환경에서는 해당 zTXt 청크의 `text`가 비어 있다.
 */
export async function extractPngTextChunksDecoded(bytes: Uint8Array): Promise<PngTextChunk[]> {
  const chunks = extractPngTextChunks(bytes);
  const decoder = new TextDecoder("utf-8");
  await Promise.all(
    chunks.map(async (chunk) => {
      if (chunk.text === undefined && chunk.compressed) {
        const inflated = await inflateZlib(chunk.compressed);
        if (inflated) chunk.text = decoder.decode(inflated);
      }
    }),
  );
  return chunks;
}
